"use client";

import type { PropsWithChildren, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { useLayout } from "@/contexts/layout-context";
import { useFocusMode } from "@/hooks/use-focus-mode";
import { cn } from "@/utils/lib";

export const StickyBox = ({ children }: PropsWithChildren) => {
  const [top, setTop] = useState<"auto" | number>(0);
  const [bottom, setBottom] = useState<"auto" | number>(0);
  const boxRef = useRef<HTMLDivElement>(null!);
  const placeholderRef = useRef<HTMLDivElement>(null!);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);

  useEffect(() => {
    let prevScrollY = window.scrollY;

    const handleSidebar = () => {
      const windowInnerHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const scrollDirection = scrollY >= prevScrollY ? "down" : "up";
      const contentHeight = boxRef.current.getBoundingClientRect().height;
      const position = windowInnerHeight - contentHeight;
      const contentOffsetTop = boxRef.current.offsetTop;

      if (windowInnerHeight > contentHeight) {
        setTop(0);
        setBottom("auto");
        setPlaceholderHeight(0);
      } else if (scrollDirection === "down") {
        setTop(position);
        setBottom("auto");
        setPlaceholderHeight(contentOffsetTop);
      } else if (scrollDirection === "up") {
        setTop("auto");
        setBottom(position);
        setPlaceholderHeight(contentOffsetTop);
      }

      prevScrollY = scrollY;
    };

    // Set initial
    handleSidebar();

    window.addEventListener("scroll", handleSidebar);
    window.addEventListener("resize", handleSidebar);
    window.addEventListener("popstate", handleSidebar);
    return () => {
      window.removeEventListener("scroll", handleSidebar);
      window.removeEventListener("resize", handleSidebar);
      window.removeEventListener("popstate", handleSidebar);
    };
  }, []);

  return (
    <>
      <div
        ref={placeholderRef}
        className="size-full"
        style={{ maxHeight: placeholderHeight }}
      />
      <div
        ref={boxRef}
        className="sticky h-fit w-full"
        style={{
          top,
          bottom,
        }}
      >
        {children}
      </div>
    </>
  );
};

export const RightSidebarWrapper = ({
  children,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { rightSidebar } = useLayout();
  const { focusedClassName } = useFocusMode();

  return !rightSidebar ? null : (
    <div
      className={cn(
        `
          relative hidden w-right-sidebar flex-col justify-start pl-[2px]
          lg:flex
        `,
        focusedClassName,
      )}
    >
      <StickyBox>{children}</StickyBox>
    </div>
  );
};
